/**
 * ImportFactFindModal — upload a fact-find/compliance document, extract every field
 * (deduped + connected to the client's existing graph), review, then commit into the
 * knowledge graph. Renders its own trigger button + dialog; state lives in
 * useFactFindImport.
 */
import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';

import { useFactFindImport } from './hooks/useFactFindImport';
import ImportReviewList from './ImportReviewList';

interface ImportFactFindModalProps {
  clientId: string;
  /** Called when the modal closes after finishing. `changed` is true only when the
   *  graph was actually written to (so the caller can skip reloading on a no-op /
   *  already-imported file). */
  onImported?: (changed: boolean) => void;
}

const ACCEPT = '.pdf,.docx';

const ImportFactFindModal: React.FC<ImportFactFindModalProps> = ({ clientId, onImported }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    phase,
    error,
    fileName,
    entities,
    includedCount,
    result,
    notice,
    start,
    toggleEntity,
    updateProp,
    commit,
    reset,
  } = useFactFindImport(clientId);

  const busy = phase === 'uploading' || phase === 'extracting' || phase === 'committing';

  const handleClose = () => {
    if (busy) return; // don't abandon an in-flight request
    setOpen(false);
    setFile(null);
    reset();
  };

  const handleDone = () => {
    // Only signal a change when the graph was actually written to — an
    // already-imported file (notice) or a 0-item commit changes nothing.
    const changed = !notice && (result?.instances_inserted ?? 0) > 0;
    setOpen(false);
    setFile(null);
    reset();
    onImported?.(changed);
  };

  const renderContent = () => {
    if (phase === 'error') {
      return <Alert severity="error">{error || 'Something went wrong.'}</Alert>;
    }
    if (phase === 'uploading' || phase === 'extracting') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
          <CircularProgress size={24} />
          <Typography variant="body2">
            {phase === 'uploading'
              ? `Uploading ${fileName}…`
              : `Extracting fields from ${fileName}… this can take a minute for long documents.`}
          </Typography>
        </Box>
      );
    }
    if (phase === 'committing') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
          <CircularProgress size={24} />
          <Typography variant="body2">Importing into the knowledge graph…</Typography>
        </Box>
      );
    }
    if (phase === 'done') {
      if (notice) {
        return <Alert severity="info">{notice}</Alert>;
      }
      return (
        <Alert severity="success">
          Imported {result?.instances_inserted ?? 0} item(s) into the client's fact find.
        </Alert>
      );
    }
    if (phase === 'review') {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Review the extracted information. Items marked <strong>Merge</strong> will update
            existing entries; <strong>New</strong> items will be added. Uncheck anything you
            don't want to import, and edit values inline.
          </Typography>
          <ImportReviewList
            entities={entities}
            onToggle={toggleEntity}
            onUpdateProp={updateProp}
          />
        </Box>
      );
    }
    // idle
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a completed fact-find or compliance document (PDF or DOCX). Its fields will
          be extracted and matched against this client's existing information for review
          before anything is saved.
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
            Choose file
          </Button>
          <Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'}>
            {file ? file.name : 'No file selected'}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          You can upload one file at a time. Re-uploading the same file won't create duplicates.
        </Typography>
      </Box>
    );
  };

  const renderActions = () => {
    if (phase === 'done') {
      return <Button variant="contained" onClick={handleDone}>Done</Button>;
    }
    if (phase === 'error') {
      return (
        <>
          <Button onClick={handleClose}>Close</Button>
          <Button variant="contained" onClick={reset}>Try again</Button>
        </>
      );
    }
    if (phase === 'review') {
      return (
        <>
          <Button variant="outlined" onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={includedCount === 0}
            onClick={commit}
          >
            Import {includedCount} item{includedCount === 1 ? '' : 's'}
          </Button>
        </>
      );
    }
    // idle / busy
    return (
      <>
        <Button variant="outlined" onClick={handleClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!file || busy}
          onClick={() => file && start(file)}
        >
          Extract
        </Button>
      </>
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<UploadFileIcon fontSize="small" />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
      >
        Import Document
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Import Fact-Find Document
          <IconButton onClick={handleClose} size="small" disabled={busy}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderContent()}</DialogContent>
        <DialogActions>{renderActions()}</DialogActions>
      </Dialog>
    </>
  );
};

export default ImportFactFindModal;
